import {Box, HStack, Image, Stack, Text, VStack} from 'platform/foundation';
import {css, useTheme} from 'styled-components';

import {suffixTestId, TestIdProps} from 'shared/web';

import {i18n} from '../../../i18n/i18n';

// The build copies `src/assets` to `/assets`; `.svg` module imports become React components here,
// so image URLs are referenced by path, as in `CebiaBanner`.
const IMAGES_PATH = '/assets/images';

// Round flags from the design; the platform `flags/*` icons are rectangular and leave bands in a circle.
// Decorative: the data-sources label next to them carries the meaning.
const COUNTRY_FLAG_CODES = ['nl', 'be', 'es', 'fr', 'de', 'it', 'at', 'hu'];
const TOTAL_COUNTRIES = 35;
const MORE_COUNTRIES_LABEL = `+${TOTAL_COUNTRIES - COUNTRY_FLAG_CODES.length}`;

const FLAG_SIZE = 6;
// Each flag covers this much of the previous one, so a flag's grid track is the size minus the
// overlap and the row needs that much extra room for the last flag.
const FLAG_OVERLAP = 2;
const FLAG_TRACK_SIZE = 4;
const FLAG_BORDER_WIDTH = '1px';

// Heights follow the design: the DAT mark is square, so it is taller than the two wordmarks.
const PARTNER_LOGOS = [
  {name: 'Cebia', file: 'partner-cebia.svg', height: 6},
  {name: 'carVertical', file: 'partner-carvertical.svg', height: 6},
  {name: 'DAT', file: 'partner-dat.svg', height: 12},
] as const;

export function HistoryCheckDataSources(props: TestIdProps) {
  return (
    <Stack
      direction={['column', 'row', 'row', 'row']}
      spacing={[6, 10, 10, 10]}
      align={['flex-start', 'center', 'center', 'center']}
      data-testid={suffixTestId('dataSources', props)}
    >
      <Box flex={1}>
        <Stack
          direction={['column', 'row', 'row', 'row']}
          spacing={[4, 10, 10, 10]}
          align={['flex-start', 'center', 'center', 'center']}
        >
          <VStack spacing={1}>
            <Text alternative>{i18n.t('historyCheck.dataSources')}</Text>
            <Text size="xSmall" color="secondary" alternative>
              {i18n.t('historyCheck.dataSourcesDescription')}
            </Text>
          </VStack>
          <HStack spacing={3} align="center">
            <CountryFlags />
            <Text size="small" alternative>
              {MORE_COUNTRIES_LABEL}
            </Text>
          </HStack>
        </Stack>
      </Box>
      <Box flex={1}>
        <HStack spacing={[6, 12, 12, 12]} align="center" wrap>
          {PARTNER_LOGOS.map((logo) => (
            <Image
              key={logo.name}
              src={`${IMAGES_PATH}/${logo.file}`}
              alt={logo.name}
              height={logo.height}
              width="auto"
            />
          ))}
        </HStack>
      </Box>
    </Stack>
  );
}

function CountryFlags() {
  const theme = useTheme();

  return (
    <div
      // Grid tracks narrower than the flags make each flag cover the previous one; the padding
      // reserves room for the last flag's overhang.
      css={css`
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: ${theme.getSize(FLAG_TRACK_SIZE)};
        padding-right: ${theme.getSize(FLAG_OVERLAP)};
      `}
    >
      {COUNTRY_FLAG_CODES.map((code, index) => (
        <div
          key={code}
          // Earlier flags stay on top of the ones that follow.
          css={css`
            position: relative;
            z-index: ${COUNTRY_FLAG_CODES.length - index};
            width: ${theme.getSize(FLAG_SIZE)};
            height: ${theme.getSize(FLAG_SIZE)};
            border: ${FLAG_BORDER_WIDTH} solid ${theme.colors.border.emphasisDefault};
            border-radius: 50%;
            overflow: hidden;
          `}
        >
          <Image
            src={`${IMAGES_PATH}/flags/flag-${code}.svg`}
            alt=""
            width="100%"
            height="100%"
            fit="cover"
          />
        </div>
      ))}
    </div>
  );
}
